import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import Calendar from "react-calendar";
import ReactLoading from "react-loading";
import * as helpers from "../../utils/utils.js";
import "./bookingCalendar.css";
import { useQuery, useQueryClient } from "react-query";
import { useAvailability } from "../../hooks/useAvailability.js";
import SlotsForDate from "../slotsForDate/slotsForDate.js";
import EditModal from "../../components/editModal/editModal.js";
import { useModal } from "../../hooks/useModal.js";
import { FaPhone } from "react-icons/fa";

const timeZone = 'UTC'; // ET zone

let today = new Date();

function BookingCalendar({  
  selectedDates, 
  addDate, 
  removeDate, 
  clearDates,
  selectedSlots,
  addSlot,
  removeSlot,
  clientPhone,
  selectionSectionRef,
}) {

  const queryClient = useQueryClient(); // ✅ initialize correctly
  const {show: showContactModal, close: closeContactModal, open: openContactModal} = useModal();
  const { fetchAvailability } = useAvailability();

  const legendItems = {unavailable: "unavailable", available: "available", limited: "Limited availability", selected: "selected"};

  const [firstVisible, setFirstVisible] = useState(today);

  const { firstDay, lastDay } = useMemo(() => {
    const aligned = firstVisible > today ? helpers.alignToSunday(firstVisible) : helpers.alignToSunday(today);
    return {
      firstDay: aligned,
      lastDay: aligned + 1000 * 60 * 60 * 24 * 43,
    };
  }, [firstVisible]);

  const queryKey = ["availability", firstDay, lastDay];

  const { 
    data: availability, 
    isLoading: availabilityIsLoading, 
    error: availabilityError 
  } = useQuery(
    queryKey,
    () => fetchAvailability({ firstDay, lastDay, status: "available" }),
    {
      staleTime: 1000 * 60 * 30,          // 30 minutes
      refetchOnWindowFocus: false,        // prevent re-fetch on tab switch
      cacheTime: 1000 * 60 * 60,          // 1 hour cache
    }
  );

  const fullAvailability = useMemo(() => {
    const cached = queryClient
      .getQueryCache()
      .findAll({ queryKey: ['availability'], exact: false })
      .flatMap((query) => query.state.data || []);

    // De-dupe by ID
    const seen = new Set();
    return [...cached, ...(availability || [])].filter((slot) => {
      if (seen.has(slot.id)) return false;
      seen.add(slot.id);
      return true;
    });
  }, [availability, queryClient]);


  function handleClickDay(value) {
    if (!availability) return;
    // This gives you the clicked date in local time.
    const localYear = value.getFullYear();
    const localMonth = value.getMonth();
    const localDate = value.getDate();
  
    // Construct the equivalent UTC midnight date.
    const utcMidnight = Date.UTC(localYear, localMonth, localDate); // This is a number

    if (helpers.hasDatePassed(utcMidnight)) return;

    // if (helpers.isDateValid(0, utcMidnight)) {
    //   openContactModal();
    //   return;
    // }
  
    if (selectedDates.includes(utcMidnight)) {
      removeDate(utcMidnight);
    } else {
      addDate(utcMidnight);
    }


  }

  
  /**
   * determines which classname to add to a tile depending on
   * availability of the date
   * @param {Date} date date of the tile
   * @returns {string} name of the class to be added
   */
  function updateCalenderTiles({ date }) {
    if (!availability) return "unavailable";

    const tileDate = helpers.toUtcMidnightTimestamp(date).valueOf();

    const dateIsSelected = selectedDates.includes(tileDate);

    const numAvailable = availability.filter(
      (slot) => slot.date === tileDate,
    ).length;

    const dateHasAvailability = numAvailable > 0;


    if (dateIsSelected && !helpers.hasDatePassed(tileDate)) {
      return "selected";
    } else if (dateHasAvailability && !helpers.hasDatePassed(tileDate)) {
      if (numAvailable === 1) {
        return "limited"
      } else {
        return "available";
      }
    } else {
      return "unavailable";
    }
  }

  function updateTileContent({ date }) {
    if (!availability) return "";

    
    const tileDate = helpers.toUtcMidnightTimestamp(date).valueOf()

    const todayAvailableSlots = availability.filter(
      (slot) => slot.date === tileDate,
    );


    if (todayAvailableSlots.length>0 && !helpers.hasDatePassed(tileDate)) {
      return <p>{todayAvailableSlots.length} left</p>;
    } else {
      return <p style={{ visibility: "hidden" }}>unavailable</p>;
    }
  }

  function isTileDisabled({ date }) {
    if (!availability) return true;

    const tileDate = helpers.toUtcMidnightTimestamp(date).valueOf()

    const dateHasAvailability = availability.some((slot) => {
      return parseInt(slot.date) === tileDate;
    });

    if (dateHasAvailability && !helpers.hasDatePassed(tileDate)) {
      return false;
    } else {
      return true;
    }
  }

  if (availabilityError) return <p>Error Loading Availability</p>

  return (
    <>
    <div className="calendar-container main-border">
      <div className="calendar-instructions">
        <div>
          {/* hidden button for spacing */}
          <button className="btn hidden" disabled={true}>
            Clear
          </button>
        </div>
        <div>
          <p className="section-heading">Select Dates</p>
        </div>
        <div>
          <button
            className="clear-btn btn"
            onClick={() => {
              clearDates();
            }}
            disabled={selectedDates.length < 1}
          >
            Clear
          </button>
        </div>
      </div>
      <div className="calendar-element-container">
        {(availabilityIsLoading) && (
          <div className="loading-div">
            <ReactLoading type="spin" color="#6c757d" height={60} width={60} />
          </div>
        )}

        <Calendar
          tileClassName={updateCalenderTiles}
          tileContent={updateTileContent}
          tileDisabled={isTileDisabled}
          onClickDay={handleClickDay}
          activeStartDate={firstVisible}
          onActiveStartDateChange={({ activeStartDate, action }) => {
            if (action === "next" || action === "prev") {
              setFirstVisible(activeStartDate);
            }
          }}
          minDetail={"month"}
          calendarType="gregory"
        />
      </div>

      <div className="calendar-legend">
        {Object.entries(legendItems).map(([key, value]) => (
          <div key={key} className="item">
            <div className={"secondary-border color " + key}>a</div>
            <div className="label">{value}</div>
          </div>
        ))}
      </div>
    </div>

    {fullAvailability && <SlotsForDate 
      selectedDates={selectedDates}
      selectedSlots={selectedSlots}
      addSlot={addSlot}
      removeSlot={removeSlot}
      removeDate={removeDate}
      availableSlots={fullAvailability}
      sectionRef={selectionSectionRef}
    /> }

    <EditModal show={showContactModal} onClose={closeContactModal}>
      <div className="contact-modal-content">
        <h2 className="modal-title">Last Minute Booking</h2>
        <p className="modal-text">
          We may have same-day availability.<br />
          To book this date, please contact us directly at:<br />
          <strong>
            <a
              className="btn btn-outline-primary contact-btn"
              href={"tel:" + clientPhone}
            >
              <div className="contact-btn-content" >
                <FaPhone className="icon" />
                <span>{helpers.formatPhoneNumber(clientPhone)}</span>
              </div>
              </a>
          </strong>
        </p>
        <button className="btn modal-close-btn" onClick={closeContactModal}>
          Close
        </button>
      </div>
    </EditModal>


    </>

  );
}

export default BookingCalendar;
